import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Instagram, ArrowUpRight, Play, Layers } from "lucide-react";
import { Reveal, Lines, SectionTag } from "../site/Motion";
import { api } from "../../lib/api";

const FALLBACK = [
  { src: "/assets/lifestyle-couple.webp", alt: "SWAPA nel quartiere della moda" },
  { src: "/assets/lifestyle-arco.webp", alt: "Arco della Pace, Milano" },
  { src: "/assets/lifestyle-cafe.webp", alt: "La pausa che conta" },
  { src: "/assets/lifestyle-door.webp", alt: "Pronta a partire" },
];

const SIZES_ATTR = "(max-width: 1023px) 72vw, (max-width: 1600px) 25vw, 400px";

function srcSetOf(sizes) {
  if (!sizes) return undefined;
  const set = ["small", "medium", "large"]
    .map((k) => sizes[k])
    .filter((s) => s && s.mediaUrl && s.width)
    .map((s) => `${s.mediaUrl} ${s.width}w`)
    .join(", ");
  return set || undefined;
}

function aspectOf(post) {
  const s = post.sizes || {};
  const d = s.medium || s.large || s.small;
  return d && d.width && d.height ? `${d.width}/${d.height}` : "4/5";
}

function PostMedia({ post }) {
  const sizes = post.sizes || {};
  const mediaClass = "h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]";
  const alt = post.caption ? post.caption.split("\n")[0].slice(0, 80) : "SWAPA su Instagram";

  if (post.mediaType === "VIDEO") {
    return (
      <video
        src={post.mediaUrl}
        poster={sizes.medium?.mediaUrl || post.thumbnailUrl || undefined}
        muted
        loop
        playsInline
        preload="none"
        aria-label={alt}
        className={mediaClass}
        onMouseEnter={(e) => e.currentTarget.play().catch(() => {})}
        onMouseLeave={(e) => {
          e.currentTarget.pause();
          e.currentTarget.currentTime = 0;
        }}
      />
    );
  }
  return (
    <img
      src={sizes.medium?.mediaUrl || post.mediaUrl}
      srcSet={srcSetOf(sizes)}
      sizes={SIZES_ATTR}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={mediaClass}
    />
  );
}

function PostTile({ post, i, reduce, y1, y2 }) {
  const isCarousel = post.mediaType === "CAROUSEL_ALBUM" && (post.children?.length || 0) > 1;
  const first = isCarousel ? post.children[0] : null;
  const display = first
    ? {
        ...post,
        mediaType: first.mediaType || "IMAGE",
        mediaUrl: first.mediaUrl || post.mediaUrl,
        thumbnailUrl: first.thumbnailUrl || post.thumbnailUrl,
        sizes: Object.keys(first.sizes || {}).length ? first.sizes : post.sizes,
      }
    : post;

  return (
    <motion.figure
      style={reduce ? {} : { y: i % 2 === 0 ? y1 : y2 }}
      className="w-[72vw] shrink-0 snap-start sm:w-[46vw] lg:mb-8 lg:w-auto lg:break-inside-avoid"
    >
      <Reveal delay={Math.min(i * 0.07, 0.42)} y={50}>
        <a
          href={post.permalink}
          target="_blank"
          rel="noreferrer"
          data-testid={`social-post-${i}`}
          aria-label={`Apri il post Instagram di SWAPA${post.mediaType === "VIDEO" ? " (video)" : ""}`}
          className="group relative block overflow-hidden border border-white/10 bg-zinc-900"
          style={{ aspectRatio: aspectOf(display) }}
        >
          <PostMedia post={display} />
          <span aria-hidden="true" className="pointer-events-none absolute inset-0 bg-black/0 transition-colors duration-300 group-hover:bg-black/20" />
          {display.mediaType === "VIDEO" && (
            <span aria-hidden="true" className="pointer-events-none absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/55 backdrop-blur transition-opacity duration-300 group-hover:opacity-0">
              <Play className="h-3.5 w-3.5 fill-white text-white" />
            </span>
          )}
          {isCarousel && (
            <span aria-hidden="true" className="pointer-events-none absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 backdrop-blur">
              <Layers className="h-3.5 w-3.5 text-white" />
            </span>
          )}
          <span aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/40 bg-black/40 backdrop-blur">
              <Instagram className="h-5 w-5 text-white" />
            </span>
          </span>
        </a>
      </Reveal>
    </motion.figure>
  );
}

function Skeleton() {
  return (
    <div data-testid="social-loading" className="mt-16 grid grid-cols-2 gap-5 lg:grid-cols-4 lg:gap-8">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className={`aspect-[4/5] animate-pulse border border-white/10 bg-white/5 ${i === 0 ? "lg:col-span-2" : ""}`} />
      ))}
    </div>
  );
}

function EditorialFallback({ reduce, y1, y2 }) {
  return (
    <div data-testid="social-fallback" className="mt-16 grid grid-cols-2 gap-5 sm:gap-8 lg:grid-cols-4">
      {FALLBACK.map((s, i) => (
        <motion.figure key={s.src} style={reduce ? {} : { y: i % 2 === 0 ? y1 : y2 }} className={i % 2 === 1 ? "lg:mt-16" : ""}>
          <Reveal delay={i * 0.08} y={50}>
            <div className="overflow-hidden border border-white/10">
              <img src={s.src} alt={s.alt} loading="lazy" decoding="async" className="aspect-[3/4] w-full object-cover" />
            </div>
          </Reveal>
        </motion.figure>
      ))}
    </div>
  );
}

export function SocialInstagram() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [feed, setFeed] = useState(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], ["4%", "-4%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);

  useEffect(() => {
    api.get("/instagram/posts").then((r) => setFeed(r.data)).catch(() => setFeed({ posts: [] }));
  }, []);

  const posts = feed?.posts || [];
  const live = posts.length > 0;
  const profileUrl = feed?.profileUrl || "https://www.instagram.com/swapa_italia/";

  return (
    <section id="social" data-testid="social-section" ref={ref} className="relative overflow-hidden border-t border-white/10 bg-ink py-28 sm:py-40">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <SectionTag index="08" label="Social / Instagram" />
        <div className="mt-10 flex flex-wrap items-end justify-between gap-8">
          <div>
            <Reveal>
              <p data-testid="social-eyebrow" className="font-mono text-[10px] uppercase tracking-[0.3em] text-swapa-yellow">@SWAPA</p>
            </Reveal>
            <h2 className="mt-4 font-display font-extrabold uppercase leading-[0.92] tracking-tight">
              <Lines lines={["SWAPA", "IN THE CITY."]} stagger={0.14} className="text-4xl text-white sm:text-6xl lg:text-7xl" />
            </h2>
          </div>
          {live && feed.followersCount > 0 && (
            <Reveal delay={0.2}>
              <p data-testid="social-followers" className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                {feed.followersCount.toLocaleString("it-IT")} follower — aggiornata in automatico da Instagram
              </p>
            </Reveal>
          )}
        </div>

        {feed === null ? (
          <Skeleton />
        ) : live ? (
          <div
            data-testid="social-gallery"
            className="mt-16 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:block lg:columns-4 lg:gap-8 lg:space-y-0 lg:overflow-visible lg:pb-0"
          >
            {posts.map((p, i) => (
              <PostTile key={p.id} post={p} i={i} reduce={reduce} y1={y1} y2={y2} />
            ))}
          </div>
        ) : (
          <EditorialFallback reduce={reduce} y1={y1} y2={y2} />
        )}

        <Reveal delay={0.15}>
          <div className="mt-16 flex justify-center">
            <a
              href={profileUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="social-follow-cta"
              className="group inline-flex items-center gap-4 border border-white/15 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.28em] text-zinc-200 transition-colors duration-300 hover:border-swapa-yellow hover:text-swapa-yellow"
            >
              <Instagram className="h-4 w-4" />
              Follow @SWAPA
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
